import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import { Grid, TextField, Paper, Typography, Box, Divider } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useJsonTemplate } from '../context/JsonTemplateContext';
import { JsonTemplateProvider } from '../context/JsonTemplateContext';
import Helper from '../../../utils/helper';
import { JsonbuilderServiceCommand } from '../../../services/command';
import { DJBE, djbeCls } from '../dynamicJsonBuilder.classes';

interface RouteParams {
    templateId: string;
}

const EditJsonTemplateContent: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { templateId } = useParams<RouteParams>();
    const { getTemplateById } = useJsonTemplate();

    const [name, setName] = useState('');
    const [templateKey, setTemplateKey] = useState('');
    const [teamName, setTeamName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const loadTemplateData = async () => {
            try {
                const templateData = await getTemplateById(Number(templateId));
                console.log('Template Data:', templateData);
                if (templateData) {
                    setName(templateData.templateName || '');
                    setTemplateKey(templateData.templateKey || '');
                    setTeamName(templateData.teamName || '');
                }
            } catch (error) {
                console.error('Error loading template data:', error);
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: t('general.somethingWentWrong'),
                });
                history.push('/json-template');
            }
        };
        loadTemplateData();
    }, []);

    const handleUpdate = async () => {
        if (!name || !templateKey || !teamName) {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.fillInput'),
            });
            return;
        }

        try {
            setIsLoading(true);
            const request = {
                id: Number(templateId),
                templateName: name,
                templateKey: templateKey,
                teamName: teamName,
                createdBy: Helper.getUserName() || ''
            };

            await JsonbuilderServiceCommand.UpdateTemplate(request);

            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: t('jsonTemplateBuilder.templateUpdated'),
            }).then(() => {
                history.push('/json-template');
            });
        } catch (error) {
            console.error('Error updating template:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.error'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        history.push('/json-template');
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={t('jsonTemplateBuilder.editJsonTemplate')}
                components={[
                    {
                        title: t('jsonTemplateBuilder.jsonTemplateBuilder'),
                        link: '/json-template',
                    },
                    {
                        title: t('jsonTemplateBuilder.editJsonTemplate'),
                        link: `/json-template/edit-json-template/${templateId}`,
                    }
                ]}
            />
            <div className={djbeCls('lineWrapper', DJBE.divider)} />
            <div className={DJBE.shell} style={{ flex: 1, padding: '24px' }}>
                <Paper className={DJBE.paper} elevation={0}
                    sx={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}>
                    <Box className={DJBE.body} p={3}>
                        <Typography className={DJBE.title} variant="h5"
                            gutterBottom
                            sx={{
                                color: '#0288d1',
                                fontWeight: 500,
                                mb: 3
                            }}>
                            {t('jsonTemplateBuilder.editJsonTemplate')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <Grid className={DJBE.fieldsGrid} container spacing={3}>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBE.inputTextfieldTemplateName}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.templateName')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBE.inputTextfieldTemplateKey}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.templateKey')}
                                    value={templateKey}
                                    onChange={(e) => setTemplateKey(e.target.value)}
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBE.inputTextfieldTeamName}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.teamName')}
                                    value={teamName}
                                    onChange={(e) => setTeamName(e.target.value)}
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                className={DJBE.actionsGrid}
                                display="flex"
                                justifyContent="flex-end"
                                gap={2}
                                size={{
                                    xs: 12
                                }}>
                                <SCUIButton
                                    className={DJBE.inputButtonCancel}
                                    onClick={handleCancel}
                                    type="secondary"
                                    sizeType="fixed"
                                    size="s"
                                    text={t('general.cancel')}
                                />
                                <SCUIButton
                                    className={DJBE.inputButtonUpdate}
                                    onClick={handleUpdate}
                                    type="primary"
                                    sizeType="fixed"
                                    size="s"
                                    text={t('general.update')}
                                    disabled={isLoading}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </div>
        </SCUIContent>
    );
};

const EditJsonTemplate: FC = () => {
    return (
        <JsonTemplateProvider>
            <EditJsonTemplateContent />
        </JsonTemplateProvider>
    );
};

export default EditJsonTemplate; 